import React, { useMemo, useState } from 'react';
import { AutoComplete } from 'antd';
import { useIntl } from 'umi';
import { filterRoleSuggestions } from '@/utils/contacts';

type RoleInputProps = {
  /** Injected by Form.Item so the label points at the input. */
  id?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  /** Fired when the user picks a suggestion or leaves the field. */
  onCommit?: (value: string) => void;
  suggestions: string[];
  autoFocus?: boolean;
};

/** Free-text role, suggesting the partner's roles first, then roles already in use. */
const RoleInput: React.FC<RoleInputProps> = ({ id, value, onChange, onCommit, suggestions, autoFocus }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  // "Use …" only for a role the user typed, not for the value the field came with.
  const [typed, setTyped] = useState(false);
  const term = (value || '').trim();

  const options = useMemo(() => {
    // Leaves out the role already in the field.
    const matches = filterRoleSuggestions(suggestions, term).map((role) => ({ value: role, label: role }));
    if (!term || !typed) return matches;
    return [
      {
        value: term,
        label: intl.formatMessage(
          { id: 'component.contacts.useRole', defaultMessage: 'Use “{role}”' },
          { role: term },
        ),
      },
      ...matches,
    ];
  }, [suggestions, term, typed, intl]);

  return (
    <AutoComplete
      id={id}
      value={value || ''}
      options={options}
      open={open && options.length > 0}
      // Not on focus: an autofocused field would cover the buttons below it.
      onClick={() => setOpen(true)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') setOpen(true);
        // An open suggestion list closes on Escape; the drawer around it stays.
        if (event.key === 'Escape' && open && options.length > 0) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
      onBlur={() => {
        setOpen(false);
        setTyped(false);
        onCommit?.(term);
      }}
      onChange={(next) => {
        setOpen(true);
        setTyped(true);
        onChange?.(next);
      }}
      onSelect={(next) => {
        setOpen(false);
        setTyped(false);
        onChange?.(next);
        onCommit?.(next);
      }}
      placeholder={intl.formatMessage({
        id: 'component.contacts.rolePlaceholder',
        defaultMessage: 'e.g. Building owner',
      })}
      autoFocus={autoFocus}
      allowClear
    />
  );
};

export default React.memo(RoleInput);
